import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from '../../config/supabase';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import { useNavigate } from "react-router-dom";
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

export default function GeneralSettingsPage() {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);

  // Options disponibles pour le paramètre tile
  const tileOptions = [
    { value: 'osm', label: 'OpenStreetMap' },
    { value: 'stradiamaps', label: 'Stadia Maps' }
  ];

  // Charger les paramètres
  useEffect(() => {
    const fetchParameters = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('parameters')
          .select('*')
          .order('parameter', { ascending: true });

        if (error) throw error;
        setParameters(data || []);
      } catch (err) {
        console.error('Erreur lors du chargement des paramètres:', err);
        setError(t('settings.admin.generalSettings.loadError'));
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) {
      fetchParameters();
    }
  }, [isAdmin, t]);

  // Mettre à jour un paramètre
  const handleUpdateParameter = async (parameter, newValue) => {
    try {
      setSaving({ ...saving, [parameter]: true });
      
      const { error } = await supabase
        .from('parameters')
        .update({ value: newValue })
        .eq('parameter', parameter);

      if (error) throw error;

      // Mettre à jour l'état local
      setParameters(prev => 
        prev.map(p => p.parameter === parameter ? { ...p, value: newValue } : p)
      );

      // Notification de succès
      alert(t('settings.admin.generalSettings.updateSuccess'));
    } catch (err) {
      console.error('Erreur lors de la mise à jour du paramètre:', err);
      alert(t('settings.admin.generalSettings.updateError'));
    } finally {
      setSaving({ ...saving, [parameter]: false });
    }
  };

  if (adminLoading || loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title={t("settings.admin.generalSettings.title")} backPath="/settings" />

      <div className="p-4">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {parameters.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {t('settings.admin.generalSettings.noParameters')}
            </div>
          ) : (
            <div className="divide-y">
              {parameters.map((param) => (
                <div key={param.parameter} className="p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {param.parameter === 'city' ? t('settings.admin.generalSettings.cityLabel') : param.parameter}
                  </label>
                  
                  {param.parameter === 'tile' ? (
                    // Liste déroulante pour le paramètre tile
                    <select
                      value={param.value || ''}
                      onChange={(e) => handleUpdateParameter(param.parameter, e.target.value)}
                      disabled={saving[param.parameter]}
                      className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-[#2894D8] focus:border-transparent disabled:opacity-50"
                    >
                      {tileOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    // Input texte pour les autres paramètres
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={param.value || ''}
                        onChange={(e) => {
                          // Mise à jour locale uniquement (pas de sauvegarde automatique)
                          setParameters(prev =>
                            prev.map(p => p.parameter === param.parameter ? { ...p, value: e.target.value } : p)
                          );
                        }}
                        disabled={saving[param.parameter]}
                        className="flex-1 border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-[#2894D8] focus:border-transparent disabled:opacity-50"
                      />
                      <button
                        onClick={() => handleUpdateParameter(param.parameter, param.value)}
                        disabled={saving[param.parameter]}
                        className="px-4 py-2 bg-[#2894D8] text-white rounded-lg hover:bg-[#136E9D] transition-colors disabled:opacity-50"
                      >
                        {saving[param.parameter] ? t('common.saving') : t('common.save')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
